import { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import { useAuth } from './useAuth'

interface Company {
  id: string
  name: string
  owner_user_id: string
  industry?: string
  timezone?: string
  subscription_tier: 1 | 2 | 3
  created_at: number
  updated_at: number
  member_count: number
  team_count: number
  location_count: number
  user_role: 'OWNER' | 'ADMIN' | 'MEMBER'
}

interface CompanyContextType {
  currentCompany: Company | null
  availableCompanies: Company[]
  loading: boolean
  switchCompany: (companyId: string) => void
  refreshCompanies: () => Promise<void>
  joinCompany: (companyCode: string) => Promise<void>
  createCompany: (companyName: string) => Promise<void>
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined)

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null)
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)

  // Fetch companies from API
  const refreshCompanies = async () => {
    if (!user || authLoading) return

    try {
      setLoading(true)
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/companies`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch companies')
      }

      const data = await response.json()
      const companies = data.companies || []
      
      setAvailableCompanies(companies)
      
      // Set current company (try to restore from localStorage first)
      const savedCompanyId = localStorage.getItem('current_company_id')
      const currentCompanyData = savedCompanyId 
        ? companies.find((c: Company) => c.id === savedCompanyId) 
        : companies[0]
      
      if (currentCompanyData) {
        setCurrentCompany(currentCompanyData)
        localStorage.setItem('current_company_id', currentCompanyData.id)
      } else {
        setCurrentCompany(null)
        localStorage.removeItem('current_company_id')
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error)
      setAvailableCompanies([])
      setCurrentCompany(null)
    } finally {
      setLoading(false)
    }
  }

  // Load companies when user is available
  useEffect(() => {
    if (!authLoading && user) {
      refreshCompanies()
    } else if (!authLoading && !user) {
      setLoading(false)
      setAvailableCompanies([])
      setCurrentCompany(null)
    }
  }, [user, authLoading])

  const switchCompany = (companyId: string) => {
    const company = availableCompanies.find(c => c.id === companyId)
    if (company) {
      setCurrentCompany(company)
      localStorage.setItem('current_company_id', companyId)
    }
  }

  const joinCompany = async (companyCode: string) => {
    // TODO: Implement company join functionality
    try {
      // Mock validation - in real app this would be an API call to join by invitation code
      if (companyCode.length !== 6) {
        throw new Error('Invalid company code')
      }

      // This would be a real API call to join a company via invitation
      throw new Error('Company joining not yet implemented')
    } catch (error) {
      throw new Error('Failed to join company. Please check the company code.')
    }
  }

  const createCompany = async (companyName: string) => {
    // Redirect to onboarding flow for new company creation
    window.location.href = '/onboarding?company=' + encodeURIComponent(companyName)
  }

  return (
    <CompanyContext.Provider value={{
      currentCompany,
      availableCompanies,
      loading,
      switchCompany,
      refreshCompanies,
      joinCompany,
      createCompany
    }}>
      {children}
    </CompanyContext.Provider>
  )
}

export function useCompany() {
  const context = useContext(CompanyContext)
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider')
  }
  return context
}